/**
 * TextureConfig - Global texture resolution configuration
 *
 * Controls which resolution variant of textures to load.
 * Available resolutions: 2048 (original), 1024, 512
 *
 * Usage:
 *   import TextureConfig from './utils/TextureConfig.js';
 *
 *   // Get/set resolution
 *   TextureConfig.resolution = 1024;  // Use 1024x1024 textures
 *   console.log(TextureConfig.resolution);
 *
 *   // Resolve a texture path to the appropriate resolution
 *   const path = TextureConfig.resolvePath('textures/brick/adobe_brick_1.webp');
 *   // Returns 'textures/brick/adobe_brick_1_1024.webp' if resolution is 1024
 *
 *   // Available resolutions
 *   console.log(TextureConfig.availableResolutions); // [2048, 1024, 512]
 */

import Debug from './Debug.js';

class TextureConfigClass {
  constructor() {
    // Default to 1024 for faster loading
    this._resolution = 512;

    // Available resolution variants
    this._availableResolutions = [2048, 1024, 512];

    // Cache of checked paths (to avoid checking same path multiple times)
    this._pathCache = new Map();

    // Preloaded path availability (set by manifest or startup check)
    this._availablePaths = null;
  }

  /**
   * Get the current texture resolution setting
   * @returns {number} Current resolution (2048, 1024, or 512)
   */
  get resolution() {
    return this._resolution;
  }

  /**
   * Set the texture resolution
   * @param {number} value - Resolution to use (2048, 1024, or 512)
   */
  set resolution(value) {
    if (this._availableResolutions.includes(value)) {
      this._resolution = value;
      // Clear cache when resolution changes
      this._pathCache.clear();
    } else {
      Debug.warn('TextureConfig', `Invalid resolution ${value}. Available: ${this._availableResolutions.join(', ')}`);
    }
  }

  /**
   * Get available resolution options
   * @returns {number[]} Array of available resolutions
   */
  get availableResolutions() {
    return [...this._availableResolutions];
  }

  /**
   * Check if a path is a texture in a category folder (eligible for resolution variants)
   * @param {string} texturePath - The texture path
   * @returns {boolean} True if path is in a texture category folder
   */
  isCategorizableTexture(texturePath) {
    if (!texturePath || typeof texturePath !== 'string') return false;

    // Must be a .webp file
    if (!texturePath.endsWith('.webp')) return false;

    // Must be in textures/<category>/ format (not root textures/ or thumbs/)
    const match = texturePath.match(/^textures\/([^/]+)\/[^/]+\.webp$/);
    if (!match) return false;

    // Exclude thumbs folder
    const category = match[1];
    if (category === 'thumbs') return false;

    // Must not already have a resolution suffix
    const filename = texturePath.split('/').pop();
    const base = filename.replace('.webp', '');
    if (base.match(/_(?:512|1024)$/)) return false;

    return true;
  }

  /**
   * Resolve a texture path to the appropriate resolution variant
   * @param {string} texturePath - Original texture path (e.g., 'textures/brick/adobe_brick_1.webp')
   * @returns {string} Resolved path with resolution suffix if applicable
   */
  resolvePath(texturePath) {
    // Return original if using 2048 (original) resolution
    if (this._resolution === 2048) {
      return texturePath;
    }

    // Check if this is a categorizable texture
    if (!this.isCategorizableTexture(texturePath)) {
      return texturePath;
    }

    // Check cache
    const cacheKey = `${texturePath}:${this._resolution}`;
    if (this._pathCache.has(cacheKey)) {
      return this._pathCache.get(cacheKey);
    }

    // Generate resolution-specific path
    const ext = '.webp';
    const basePath = texturePath.slice(0, -ext.length);
    const resolvedPath = `${basePath}_${this._resolution}${ext}`;

    // Cache and return
    this._pathCache.set(cacheKey, resolvedPath);
    return resolvedPath;
  }

  /**
   * Preload texture availability from manifest or file list
   * This allows for synchronous path resolution with fallback
   * @param {string[]} availablePaths - Array of available texture paths
   */
  setAvailablePaths(availablePaths) {
    this._availablePaths = new Set(availablePaths);
  }

  /**
   * Resolve path with fallback to original if resolution variant doesn't exist
   * Requires setAvailablePaths to have been called first
   * @param {string} texturePath - Original texture path
   * @returns {string} Resolved path (with fallback to original)
   */
  resolvePathWithFallback(texturePath) {
    const resolved = this.resolvePath(texturePath);

    // If we have availability info, check if resolved path exists
    if (this._availablePaths && resolved !== texturePath) {
      if (!this._availablePaths.has(resolved)) {
        return texturePath; // Fallback to original
      }
    }

    return resolved;
  }

  /**
   * Clear the path cache (call when changing resolution or reloading textures)
   */
  clearCache() {
    this._pathCache.clear();
  }
}

// Singleton instance
const TextureConfig = new TextureConfigClass();

// Expose for easy configuration in browser console
if (typeof window !== 'undefined') {
  window.TextureConfig = TextureConfig;
}

export default TextureConfig;
